/**
 * Manual data capture for figures no system holds: define a dataset, then
 * collect it per school per year by Excel upload or a form. Every field
 * becomes a measure in the Indicator Studio.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, List, ListItemButton,
  ListItemText, MenuItem, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, DeleteOutline, Download, Edit, EditNote, Upload } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Catalogue, Dataset, DatasetField, DatasetUpload, SchoolOption, datasetApi, errorMessage,
  indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';

const LAST_YEAR = new Date().getFullYear() - 1;
const YEARS = Array.from({ length: 6 }, (_, i) => LAST_YEAR + 1 - i);
const slug = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

// ── Dataset definition ───────────────────────────────────────────────────────

const DatasetEditor: React.FC<{ initial?: Dataset; onClose: (saved?: Dataset) => void }> = ({ initial, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: initial?.code || '',
    name: initial?.name || '',
    description: initial?.description || '',
    is_active: initial?.is_active ?? true,
    dataset_fields: initial?.dataset_fields || [{ code: '', label: '', unit: '', help_text: '' }] as DatasetField[],
  });
  const setField = (i: number, patch: Partial<DatasetField>) => setForm(f => ({
    ...f, dataset_fields: f.dataset_fields.map((x, j) => (j === i ? { ...x, ...patch } : x)),
  }));

  const save = useMutation({
    mutationFn: () => (initial ? datasetApi.update(initial.id, form) : datasetApi.create(form)),
    onSuccess: saved => {
      queryClient.invalidateQueries(['datasets']);
      queryClient.invalidateQueries(['indicator-registry', 'catalogue']);
      enqueueSnackbar(`${saved.name} saved`, { variant: 'success' });
      onClose(saved);
    },
  });

  return (
    <Dialog open onClose={() => onClose()} fullWidth maxWidth="md">
      <DialogTitle>{initial ? `Edit ${initial.name}` : 'New dataset'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Name" size="small" fullWidth required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value, code: initial ? f.code : slug(e.target.value) }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Code" size="small" fullWidth required value={form.code} disabled={!!initial}
              helperText="Used in measure codes; can't change later"
              onChange={e => setForm(f => ({ ...f, code: slug(e.target.value) }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description" size="small" fullWidth multiline minRows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Fields (one number per school per year)</Typography>
        <Stack spacing={1}>
          {form.dataset_fields.map((f, i) => (
            <Box key={f.id ?? `new-${i}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Label" size="small" required value={f.label} sx={{ flex: 2 }}
                onChange={e => setField(i, { label: e.target.value, ...(f.id ? {} : { code: slug(e.target.value) }) })}
              />
              <TextField
                label="Code" size="small" required value={f.code} disabled={!!f.id} sx={{ flex: 1 }}
                onChange={e => setField(i, { code: slug(e.target.value) })}
              />
              <TextField label="Unit" size="small" value={f.unit} sx={{ width: 120 }} onChange={e => setField(i, { unit: e.target.value })} />
              <TextField label="Notes for data entry" size="small" value={f.help_text} sx={{ flex: 2 }} onChange={e => setField(i, { help_text: e.target.value })} />
              <IconButton
                aria-label="Remove field" disabled={form.dataset_fields.length === 1}
                onClick={() => setForm(x => ({ ...x, dataset_fields: x.dataset_fields.filter((_, j) => j !== i) }))}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
        <Button size="small" startIcon={<Add />} sx={{ mt: 1 }}
          onClick={() => setForm(f => ({ ...f, dataset_fields: [...f.dataset_fields, { code: '', label: '', unit: '', help_text: '' }] }))}
        >
          Add field
        </Button>

        {initial && (
          <FormControlLabel
            sx={{ display: 'block', mt: 2 }}
            control={<Switch checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
            label="Active (inactive datasets disappear from the measure list)"
          />
        )}
        {save.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button variant="contained" disabled={save.isLoading} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Form entry for one school ────────────────────────────────────────────────

const SchoolEntry: React.FC<{ dataset: Dataset; year: number; existing: Record<string, Record<string, number>>; onClose: () => void }> = ({
  dataset, year, existing, onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [school, setSchool] = useState<SchoolOption | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const schools = useQuery(['indicator-schools', search], () => indicatorRegistryApi.schools(search), {
    enabled: search.length >= 2, keepPreviousData: true,
  });

  useEffect(() => {
    const current = school ? existing[school.code] || {} : {};
    setValues(Object.fromEntries(dataset.dataset_fields.map(f => [f.code, current[f.code]?.toString() ?? ''])));
  }, [school, existing, dataset]);

  const save = useMutation({
    mutationFn: () => datasetApi.saveSchool(dataset.id, year, school!.code,
      Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim() === '' ? null : Number(v)]))),
    onSuccess: () => {
      queryClient.invalidateQueries(['datasets', dataset.id]);
      enqueueSnackbar(`Saved ${school!.name}`, { variant: 'success' });
      setSchool(null);
    },
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enter {dataset.name}, {year}</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={schools.data || []}
          value={school}
          filterOptions={x => x}
          getOptionLabel={s => `${s.name} (${s.code})`}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          onInputChange={(_, v) => setSearch(v)}
          onChange={(_, v) => setSchool(v)}
          renderOption={(props, s) => (
            <li {...props} key={s.code}>
              <Box>
                <Typography variant="body2">{s.name}</Typography>
                <Typography variant="caption" color="text.secondary">{s.code} · {s.district}</Typography>
              </Box>
            </li>
          )}
          noOptionsText={search.length < 2 ? 'Type at least 2 letters' : 'No active school matches'}
          renderInput={params => <TextField {...params} label="School" size="small" />}
        />
        {school && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {dataset.dataset_fields.map(f => (
              <TextField
                key={f.code} size="small" type="number" label={`${f.label}${f.unit ? ` (${f.unit})` : ''}`}
                helperText={f.help_text || 'Leave empty to clear'} value={values[f.code] ?? ''}
                inputProps={{ min: 0 }}
                onChange={e => setValues(v => ({ ...v, [f.code]: e.target.value }))}
              />
            ))}
          </Stack>
        )}
        {save.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(save.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!school || save.isLoading} onClick={() => save.mutate()}>Save school</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Upload result ────────────────────────────────────────────────────────────

const UploadResult: React.FC<{ upload: DatasetUpload; onClose: () => void }> = ({ upload, onClose }) => (
  <Alert severity={upload.accepted ? 'success' : 'error'} onClose={onClose} sx={{ mb: 2 }}>
    {upload.accepted
      ? `${upload.file_name}: saved ${upload.values_saved.toLocaleString()} values for ${upload.schools.toLocaleString()} schools.`
      : `${upload.file_name} was not saved. Fix these ${upload.errors.length}${upload.errors.length >= 200 ? '+' : ''} problems and upload it again:`}
    {!upload.accepted && (
      <TableContainer sx={{ maxHeight: 260, mt: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow><TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Problem</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {upload.errors.map((e, i) => (
              <TableRow key={i}><TableCell>{e.row ?? '—'}</TableCell><TableCell>{e.column}</TableCell><TableCell>{e.message}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Alert>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const DataCapture: React.FC<{ catalogue: Catalogue }> = ({ catalogue }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [year, setYear] = useState(LAST_YEAR);
  const [editing, setEditing] = useState<Dataset | 'new' | null>(null);
  const [entering, setEntering] = useState(false);
  const [lastUpload, setLastUpload] = useState<DatasetUpload | null>(null);

  const list = useQuery(['datasets'], datasetApi.list);
  const datasets = list.data || [];
  const dataset = datasets.find(d => d.id === selectedId) || datasets[0];

  const values = useQuery(['datasets', dataset?.id, 'values', year], () => datasetApi.values(dataset!.id, year), { enabled: !!dataset });
  const uploads = useQuery(['datasets', dataset?.id, 'uploads'], () => datasetApi.uploads(dataset!.id), { enabled: !!dataset });

  const download = useMutation({
    mutationFn: () => datasetApi.downloadTemplate(dataset!, year),
    onError: err => enqueueSnackbar(errorMessage(err), { variant: 'error' }),
  });
  const upload = useMutation({
    mutationFn: (file: File) => datasetApi.upload(dataset!.id, year, file),
    onSuccess: result => {
      setLastUpload(result);
      queryClient.invalidateQueries(['datasets', dataset!.id]);
    },
    onError: err => enqueueSnackbar(errorMessage(err), { variant: 'error' }),
  });

  const existing = Object.fromEntries((values.data?.rows || []).map(r => [r.school_code, r.values]));

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Data capture</Typography>
          <Typography variant="body2" color="text.secondary">
            Collect figures no system holds, per school and year. Each field becomes a measure in the Indicator Studio.
          </Typography>
        </Box>
        {catalogue.can_manage_datasets && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setEditing('new')}>New dataset</Button>
        )}
      </Box>

      {list.isLoading && <CircularProgress />}
      {!list.isLoading && !datasets.length && (
        <Alert severity="info">
          No datasets yet. {catalogue.can_manage_datasets
            ? 'Create one for data that SIS and TTPDS don\'t collect, such as a facilities survey.'
            : 'Ask an administrator to define one.'}
        </Alert>
      )}

      {dataset && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper variant="outlined">
              <List dense disablePadding>
                {datasets.map(d => (
                  <ListItemButton key={d.id} selected={d.id === dataset.id} onClick={() => { setSelectedId(d.id); setLastUpload(null); }}>
                    <ListItemText
                      primary={d.name}
                      secondary={`${d.dataset_fields.length} field${d.dataset_fields.length === 1 ? '' : 's'}${d.is_active ? '' : ' · inactive'}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6">{dataset.name}</Typography>
                  {dataset.description && <Typography variant="body2" color="text.secondary">{dataset.description}</Typography>}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {dataset.dataset_fields.map(f => (
                      <Chip key={f.code} size="small" variant="outlined" label={`${f.label}${f.unit ? ` (${f.unit})` : ''}`} />
                    ))}
                  </Stack>
                </Box>
                {catalogue.can_manage_datasets && (
                  <Button size="small" startIcon={<Edit />} onClick={() => setEditing(dataset)} sx={{ alignSelf: 'flex-start' }}>
                    Edit dataset
                  </Button>
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3, mb: 2 }} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Year</InputLabel>
                  <Select label="Year" value={year} onChange={e => { setYear(Number(e.target.value)); setLastUpload(null); }}>
                    {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={download.isLoading ? <CircularProgress size={16} /> : <Download />}
                  disabled={download.isLoading} onClick={() => download.mutate()}
                >
                  Download template
                </Button>
                <Button variant="contained" startIcon={upload.isLoading ? <CircularProgress size={16} color="inherit" /> : <Upload />}
                  disabled={upload.isLoading} onClick={() => fileInput.current?.click()}
                >
                  {upload.isLoading ? 'Checking…' : 'Upload filled template'}
                </Button>
                <input
                  ref={fileInput} type="file" hidden accept=".xlsx,.csv"
                  onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }}
                />
                <Button startIcon={<EditNote />} onClick={() => setEntering(true)}>Enter for one school</Button>
              </Stack>

              {lastUpload && <UploadResult upload={lastUpload} onClose={() => setLastUpload(null)} />}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {values.data ? `${values.data.schools.toLocaleString()} schools have ${year} values` : 'Values'}
              </Typography>
              {values.isFetching && <CircularProgress size={20} />}
              {values.data && values.data.rows.length > 0 && (
                <TableContainer sx={{ maxHeight: 360, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>District</TableCell>
                        {dataset.dataset_fields.map(f => <TableCell key={f.code} align="right">{f.label}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {values.data.rows.map(r => (
                        <TableRow key={r.school_code} hover>
                          <TableCell>
                            {r.school_name}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.school_code}</Typography>
                          </TableCell>
                          <TableCell>{r.district}</TableCell>
                          {dataset.dataset_fields.map(f => (
                            <TableCell key={f.code} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {r.values[f.code]?.toLocaleString() ?? '—'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {(uploads.data || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Recent uploads</Typography>
                  <Table size="small">
                    <TableBody>
                      {uploads.data!.slice(0, 8).map(u => (
                        <TableRow key={u.id}>
                          <TableCell>{new Date(u.uploaded_at).toLocaleString()}</TableCell>
                          <TableCell>{u.uploaded_by_name}</TableCell>
                          <TableCell>{u.file_name} · {u.year}</TableCell>
                          <TableCell>
                            <Chip size="small" color={u.accepted ? 'success' : 'error'} variant="outlined"
                              label={u.accepted ? `${u.values_saved.toLocaleString()} values saved` : `${u.errors.length} problems, nothing saved`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {editing && (
        <DatasetEditor
          initial={editing === 'new' ? undefined : editing}
          onClose={saved => { setEditing(null); if (saved) setSelectedId(saved.id); }}
        />
      )}
      {entering && dataset && (
        <SchoolEntry dataset={dataset} year={year} existing={existing} onClose={() => setEntering(false)} />
      )}
    </Box>
  );
};

export default DataCapture;
